import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { API, useAuth } from "@/App";
import { Button } from "@/components/ui/button";
import { 
  LayoutDashboard, Calendar, FileText, Heart, Users, 
  Ticket, Settings, LogOut, ChevronRight, DollarSign,
  TrendingUp, ArrowUpRight, Eye, UserPlus, QrCode, Mail, Camera,
  BarChart3, Target, Newspaper, Share2, FileSpreadsheet, ChevronDown, Handshake, Radio, Send, Video, Headphones, Menu, X
} from "lucide-react";
import { format } from "date-fns";

// Official Blue vs Blue Logo
const LOGO_URL = "https://customer-assets.emergentagent.com/job_6cd70ab0-164b-4026-90ab-4bbce9b2006d/artifacts/koo5s91n_BluevsBlue1%20%281%29.png";

// Grouped sidebar links for better organization
const sidebarGroups = [
  {
    title: "Overview",
    links: [
      { href: "/admin", icon: LayoutDashboard, label: "Dashboard" },
      { href: "/admin/analytics", icon: BarChart3, label: "Analytics" },
    ]
  },
  {
    title: "Sales & Events",
    links: [
      { href: "/admin/ticket-sales", icon: TrendingUp, label: "Ticket Sales" },
      { href: "/admin/events", icon: Calendar, label: "Events" },
      { href: "/admin/tickets", icon: Ticket, label: "Tickets" },
      { href: "/ticket-verify", icon: QrCode, label: "Scan Tickets" },
    ]
  },
  {
    title: "Fundraising",
    links: [
      { href: "/admin/campaigns", icon: Target, label: "Campaigns" },
      { href: "/admin/donations", icon: Heart, label: "Donations" },
    ]
  },
  {
    title: "Content",
    links: [
      { href: "/admin/blog", icon: FileText, label: "Blog" },
      { href: "/admin/gallery", icon: Camera, label: "Gallery" },
      { href: "/admin/partners", icon: Handshake, label: "Partners" },
      { href: "/admin/newsletters", icon: Newspaper, label: "Newsletters" },
      { href: "/admin/digest", icon: Send, label: "Weekly Digest" },
    ]
  },
  {
    title: "People",
    links: [
      { href: "/admin/supporters", icon: Users, label: "Supporters" },
      { href: "/admin/messages", icon: Mail, label: "Messages" },
      { href: "/admin/users", icon: UserPlus, label: "Users" },
    ]
  },
  {
    title: "System",
    links: [
      { href: "/admin/reports", icon: FileSpreadsheet, label: "Reports" },
      { href: "/admin/social-media", icon: Share2, label: "Social Media" },
      { href: "/admin/streams", icon: Radio, label: "Streams & Permissions" },
      { href: "/podcast", icon: Headphones, label: "Podcast" },
      { href: "/live", icon: Video, label: "Go Live" },
      { href: "/conference", icon: Video, label: "Conference Room" },
      { href: "/admin/settings", icon: Settings, label: "Settings" },
      { href: "/admin/site-changes", icon: FileSpreadsheet, label: "Site Changes" },
      { href: "/admin/training", icon: Target, label: "Training Guide" },
    ]
  }
];

export function AdminLayout({ children, currentPage }) {
  const { user, logout } = useAuth();
  const [collapsedGroups, setCollapsedGroups] = useState({});
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const toggleGroup = (title) => {
    setCollapsedGroups(prev => ({
      ...prev,
      [title]: !prev[title]
    }));
  };

  // Find current group for active state
  const findCurrentGroup = () => {
    for (const group of sidebarGroups) {
      if (group.links.some(link => link.label === currentPage)) {
        return group.title;
      }
    }
    return null;
  };

  return (
    <div className="min-h-screen bg-slate-50 flex">
      {/* Mobile sidebar toggle */}
      <button onClick={() => setSidebarOpen(!sidebarOpen)} className="lg:hidden fixed top-3 left-3 z-50 bg-deep-blue text-white p-2 rounded-lg shadow-lg" data-testid="admin-sidebar-toggle">
        {sidebarOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
      </button>

      {/* Sidebar overlay on mobile */}
      {sidebarOpen && <div className="lg:hidden fixed inset-0 bg-black/50 z-30" onClick={() => setSidebarOpen(false)} />}

      {/* Sidebar */}
      <aside className={`w-56 bg-deep-blue text-white flex-shrink-0 fixed h-full flex flex-col z-40 transition-transform duration-300 ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'} lg:translate-x-0`}>
        {/* Logo */}
        <div className="p-4 border-b border-white/10">
          <Link to="/" className="flex items-center gap-2">
            <img src={LOGO_URL} alt="Blue vs Blue" className="h-8 rounded" />
            <span className="font-semibold text-sm">Admin</span>
          </Link>
        </div>
        
        {/* Scrollable Navigation */}
        <nav className="flex-1 overflow-y-auto py-2 scrollbar-thin">
          {sidebarGroups.map((group) => {
            const isCollapsed = collapsedGroups[group.title];
            const isActiveGroup = findCurrentGroup() === group.title;
            
            return (
              <div key={group.title} className="mb-1">
                {/* Group Header */}
                <button
                  onClick={() => toggleGroup(group.title)}
                  className="w-full flex items-center justify-between px-4 py-2 text-xs font-semibold uppercase tracking-wider text-white/50 hover:text-white/70 transition-colors"
                >
                  <span>{group.title}</span>
                  <ChevronDown className={`w-3 h-3 transition-transform ${isCollapsed ? '-rotate-90' : ''}`} />
                </button>
                
                {/* Group Links */}
                {!isCollapsed && (
                  <div className="px-2 space-y-0.5">
                    {group.links.map((link) => (
                      <Link
                        key={link.href}
                        to={link.href}
                        onClick={() => setSidebarOpen(false)}
                        data-testid={`admin-nav-${link.label.toLowerCase().replace(' ', '-')}`}
                        className={`flex items-center gap-2.5 px-3 py-2 rounded-md text-sm transition-colors ${
                          currentPage === link.label 
                            ? 'bg-awareness-blue/20 text-awareness-blue font-medium' 
                            : 'text-white/70 hover:bg-white/5 hover:text-white'
                        }`}
                      >
                        <link.icon className="w-4 h-4 flex-shrink-0" />
                        <span className="truncate">{link.label}</span>
                      </Link>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </nav>

        {/* User Section - Fixed at bottom */}
        <div className="p-3 border-t border-white/10 bg-deep-blue">
          <div className="flex items-center gap-2 px-2 py-1.5 rounded-md bg-white/5">
            <div className="w-7 h-7 rounded-full bg-awareness-blue flex items-center justify-center text-deep-blue font-semibold text-xs flex-shrink-0">
              {user?.name?.charAt(0).toUpperCase()}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-xs font-medium truncate">{user?.name}</p>
            </div>
            <Button 
              variant="ghost" 
              size="sm"
              className="h-7 w-7 p-0 text-white/50 hover:text-white hover:bg-white/10"
              onClick={logout}
              data-testid="admin-logout"
              title="Sign Out"
            >
              <LogOut className="w-3.5 h-3.5" />
            </Button>
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 ml-0 lg:ml-56">
        {children}
      </main>
    </div>
  );
}

export default function AdminDashboard() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API}/admin/stats`, { withCredentials: true });
      setStats(response.data);
    } catch (error) {
      console.error("Error fetching stats:", error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <AdminLayout currentPage="Dashboard">
      <div className="p-8" data-testid="admin-dashboard">
        <div className="mb-8">
          <h1 className="text-heading-2 text-deep-blue">Dashboard</h1>
          <p className="text-slate-600">Welcome to the Blue vs Blue admin panel</p>
        </div>

        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[1, 2, 3, 4].map(i => (
              <div key={i} className="bg-white rounded-lg p-6 h-32 animate-pulse"></div>
            ))}
          </div>
        ) : (
          <>
            {/* Stats Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
              <div className="bg-white rounded-lg p-6 shadow-sm" data-testid="stat-donations">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-slate-500 font-medium">Total Revenue</p>
                    <p className="text-3xl font-bold text-deep-blue mt-1">
                      ${(stats?.total_revenue || 0).toLocaleString(undefined, {maximumFractionDigits: 0})}
                    </p>
                  </div>
                  <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
                    <DollarSign className="w-6 h-6 text-green-600" />
                  </div>
                </div>
                <p className="text-sm text-slate-500 mt-3">
                  ${(stats?.total_donation_amount || 0).toLocaleString()} donations · ${(stats?.ticket_revenue || 0).toLocaleString()} tickets · ${(stats?.offset_total || 0).toLocaleString()} campaign offsets
                </p>
              </div>

              <div className="bg-white rounded-lg p-6 shadow-sm" data-testid="stat-events">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-slate-500 font-medium">Tickets Sold</p>
                    <p className="text-3xl font-bold text-deep-blue mt-1">{stats?.total_tickets || 0}</p>
                  </div>
                  <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
                    <Calendar className="w-6 h-6 text-blue-600" />
                  </div>
                </div>
                <p className="text-sm text-slate-500 mt-3">
                  {stats?.paid_tickets || 0} paid · {stats?.free_tickets || 0} free · {stats?.total_orders || 0} orders
                </p>
              </div>

              <div className="bg-white rounded-lg p-6 shadow-sm" data-testid="stat-supporters">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-slate-500 font-medium">Supporters</p>
                    <p className="text-3xl font-bold text-deep-blue mt-1">{stats?.total_supporters || 0}</p>
                  </div>
                  <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center">
                    <Users className="w-6 h-6 text-purple-600" />
                  </div>
                </div>
                <p className="text-sm text-slate-500 mt-3">{stats?.total_checkins || 0} check-ins</p>
              </div>

              <div className="bg-white rounded-lg p-6 shadow-sm" data-testid="stat-blogs">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-slate-500 font-medium">Blog Posts</p>
                    <p className="text-3xl font-bold text-deep-blue mt-1">{stats?.total_blogs || 0}</p>
                  </div>
                  <div className="w-12 h-12 bg-orange-100 rounded-full flex items-center justify-center">
                    <FileText className="w-6 h-6 text-orange-600" />
                  </div>
                </div>
                <p className="text-sm text-slate-500 mt-3">Published articles</p>
              </div>
            </div>

            {/* Recent Activity */}
            <div className="grid lg:grid-cols-2 gap-6">
              {/* Recent Donations */}
              <div className="bg-white rounded-lg shadow-sm" data-testid="recent-donations">
                <div className="p-6 border-b border-slate-100 flex justify-between items-center">
                  <h2 className="text-lg font-semibold text-deep-blue">Recent Donations</h2>
                  <Link to="/admin/donations" className="text-awareness-blue text-sm font-medium flex items-center">
                    View All <ChevronRight className="w-4 h-4 ml-1" />
                  </Link>
                </div>
                <div className="divide-y divide-slate-100">
                  {stats?.recent_donations?.length > 0 ? (
                    stats.recent_donations.map((donation, index) => (
                      <div key={index} className="p-4 flex items-center justify-between">
                        <div>
                          <p className="font-medium text-slate-800">
                            {donation.anonymous ? "Anonymous" : donation.donor_name}
                          </p>
                          <p className="text-sm text-slate-500">
                            {format(new Date(donation.created_at), "MMM d, yyyy")}
                          </p>
                        </div>
                        <span className="text-lg font-semibold text-green-600">
                          ${donation.amount}
                        </span>
                      </div>
                    ))
                  ) : (
                    <div className="p-6 text-center text-slate-500">
                      No donations yet
                    </div>
                  )}
                </div>
              </div>

              {/* Upcoming Events */}
              <div className="bg-white rounded-lg shadow-sm" data-testid="upcoming-events">
                <div className="p-6 border-b border-slate-100 flex justify-between items-center">
                  <h2 className="text-lg font-semibold text-deep-blue">Upcoming Events</h2>
                  <Link to="/admin/events" className="text-awareness-blue text-sm font-medium flex items-center">
                    View All <ChevronRight className="w-4 h-4 ml-1" />
                  </Link>
                </div>
                <div className="divide-y divide-slate-100">
                  {stats?.upcoming_events?.length > 0 ? (
                    stats.upcoming_events.map((event, index) => (
                      <div key={index} className="p-4 flex items-center justify-between">
                        <div>
                          <p className="font-medium text-slate-800">{event.title}</p>
                          <p className="text-sm text-slate-500">
                            {format(new Date(event.date), "MMM d, yyyy")}
                          </p>
                        </div>
                        <span className="text-sm bg-awareness-blue/10 text-awareness-blue px-3 py-1 rounded-full">
                          {event.category}
                        </span>
                      </div>
                    ))
                  ) : (
                    <div className="p-6 text-center text-slate-500">
                      No upcoming events
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Recent Contacts */}
            {stats?.recent_contacts?.length > 0 && (
              <div className="bg-white rounded-lg shadow-sm mt-6" data-testid="recent-contacts">
                <div className="p-6 border-b border-slate-100 flex justify-between items-center">
                  <h2 className="text-lg font-semibold text-deep-blue">Recent Contact Submissions</h2>
                </div>
                <div className="divide-y divide-slate-100">
                  {stats.recent_contacts.map((contact, index) => (
                    <div key={index} className="p-4">
                      <div className="flex justify-between items-start">
                        <div>
                          <p className="font-medium text-slate-800">{contact.name}</p>
                          <p className="text-sm text-slate-500">{contact.email}</p>
                        </div>
                        <span className="text-xs bg-slate-100 text-slate-600 px-2 py-1 rounded">
                          {contact.inquiry_type}
                        </span>
                      </div>
                      <p className="text-sm text-slate-600 mt-2 line-clamp-2">{contact.message}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </AdminLayout>
  );
}
